import * as Yup from 'yup';
import React, { useState, forwardRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import { Link, Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CognitoAuthenticate, LoginData } from '../../../network/Auth';
import { URL_CONSTANT } from '../../../URLConstants';
import { useAuth } from '../../customContextProviders/AuthContext';

export default function LoginForm({ setAlertMetas, alertMetas }) {
  const navigate = useNavigate();
  const { authenticateSession, awsDataSession } = useAuth();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),

    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: (values, { resetForm }) => {
      const payload = {
        email: values.email,
        password: values.password
      };
      login(payload);
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const login = async (payload) => {
    try {
      const loginResponse = await LoginData(payload);

      authenticateSession(loginResponse?.data?.data);

      const awsTokenResponse = await CognitoAuthenticate();

      await awsDataSession(awsTokenResponse?.data?.data);

      formik.setSubmitting(false);
      navigate(URL_CONSTANT.SMARTLOCK, { replace: true });
    } catch (err) {
      console.error('Error during login or fetching AWS token:', err);

      formik.setSubmitting(false);
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: 'Please check your email or password'
      });

      // Reset alert after 4 seconds
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit} data-testid="login-form">
        <Stack spacing={3}>
          <TextField
            fullWidth
            type="email"
            autoComplete="username"
            label="Email address"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="flex-start" mt={2}>
          {/* <FormControlLabel
              control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
              label="Remember me"
            /> */}

          <Link
            component={RouterLink}
            variant="subtitle2"
            to={URL_CONSTANT.FORGOT_PASSWORD}
            sx={{
              fontSize: ' 12px',
              fontWeight: '600',
              lineHeight: '16px',
              textAlign: 'left',
              opacity: '0.5',
              color: '#000000'
            }}
          >
            Forgot password?
          </Link>
        </Stack>

        <LoadingButton
          size="medium"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          sx={{
            display: 'flex',
            margin: '24px auto 0',
            height: '100%'
          }}
        >
          Login
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
